// src/services/organizationService.js - 完全版（組織とデータ管理統合）

import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    setDoc, 
    updateDoc, 
    deleteDoc, 
    query, 
    where, 
    orderBy,
    writeBatch 
  } from 'firebase/firestore';
  import { db } from '../firebase';
  import { OrganizationModel, UserOrganizationModel, MigrationUtils } from '../models/organizationModels';
  
  /**
   * 組織管理サービス（統合版）
   * 組織の作成・管理とデータ分離の両方を担当
   */
  export class OrganizationService {
    
    // ==========================================
    // 組織管理機能
    // ==========================================
  
    /**
     * 組織を作成
     */
    static async createOrganization(organizationData, ownerId) {
      try {
        const orgData = OrganizationModel.createDefault(
          organizationData.name,
          organizationData.type
        );
        
        // カスタム設定をマージ
        if (organizationData.settings) {
          orgData.settings = { ...orgData.settings, ...organizationData.settings };
        }
        if (organizationData.contact) {
          orgData.contact = { ...orgData.contact, ...organizationData.contact };
        }
  
        // バリデーション
        const validation = OrganizationModel.validate(orgData);
        if (!validation.isValid) {
          throw new Error(validation.errors.join(', '));
        }
  
        // 組織IDを生成（名前ベース + タイムスタンプ）
        const orgId = `org_${organizationData.name.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}_${Date.now()}`;
        
        // 招待コードを生成
        const inviteCode = `invite_${orgId}_${Math.random().toString(36).substring(2, 15)}`;
        
        const batch = writeBatch(db);
        
        // 組織を作成
        const orgRef = doc(db, 'organizations', orgId);
        batch.set(orgRef, { 
          ...orgData, 
          id: orgId,
          inviteCode: inviteCode
        });
        
        // オーナーを組織に関連付け
        const userOrgData = UserOrganizationModel.createDefault(ownerId, orgId, 'owner');
        const userOrgRef = doc(db, 'userOrganizations', `${ownerId}_${orgId}`);
        batch.set(userOrgRef, userOrgData);
        
        await batch.commit();
        
        return { success: true, organizationId: orgId, data: orgData };
      } catch (error) {
        console.error('組織作成エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * ユーザーが所属する組織一覧を取得
     */
    static async getUserOrganizations(userId) {
      try {
        const userOrgsQuery = query(
          collection(db, 'userOrganizations'),
          where('userId', '==', userId),
          where('isActive', '==', true)
        );
        
        const userOrgsSnap = await getDocs(userOrgsQuery);
        const organizations = [];
        
        for (const userOrgDoc of userOrgsSnap.docs) {
          const userOrgData = userOrgDoc.data();
          
          // 組織詳細を取得
          const orgDoc = await getDoc(doc(db, 'organizations', userOrgData.organizationId));
          if (orgDoc.exists()) {
            organizations.push({
              id: orgDoc.id,
              ...orgDoc.data(),
              userRole: userOrgData.role,
              userPermissions: userOrgData.permissions,
              joinedAt: userOrgData.joinedAt
            });
          }
        }
        
        return { success: true, organizations };
      } catch (error) {
        console.error('ユーザー組織取得エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * 組織詳細を取得
     */
    static async getOrganization(organizationId) {
      try {
        const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
        
        if (!orgDoc.exists()) {
          return { success: false, error: '組織が見つかりません' };
        }
        
        return { success: true, organization: orgDoc.data() };
      } catch (error) {
        console.error('組織取得エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * 組織設定を更新
     */
    static async updateOrganization(organizationId, updateData, userId) {
      try {
        // 権限チェック
        const hasPermission = await this.checkUserPermission(userId, organizationId, 'org:write');
        if (!hasPermission) {
          return { success: false, error: '組織設定を変更する権限がありません' };
        }
  
        const orgRef = doc(db, 'organizations', organizationId);
        await updateDoc(orgRef, {
          ...updateData,
          updatedAt: new Date(),
          updatedBy: userId
        });
  
        return { success: true };
      } catch (error) {
        console.error('組織更新エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * 招待コードで組織に参加
     */
    static async joinByInviteCode(inviteCode, userId) {
      try {
        if (!inviteCode || !inviteCode.startsWith('invite_')) {
          return { success: false, error: '無効な招待コードです' };
        }
  
        // 招待コードから組織を検索
        const orgsQuery = query(
          collection(db, 'organizations'),
          where('inviteCode', '==', inviteCode)
        );
        
        const orgsSnap = await getDocs(orgsQuery);
        
        if (orgsSnap.empty) {
          return { success: false, error: '招待コードが見つかりません' };
        }
  
        const orgDoc = orgsSnap.docs[0];
        const orgData = orgDoc.data();
  
        // 既に参加しているかチェック
        const userOrgRef = doc(db, 'userOrganizations', `${userId}_${orgDoc.id}`);
        const userOrgDoc = await getDoc(userOrgRef);
        
        if (userOrgDoc.exists() && userOrgDoc.data().isActive) {
          return { success: false, error: '既にこの組織に参加しています' };
        }
  
        // ユーザーを組織に追加
        const userOrgData = UserOrganizationModel.createDefault(userId, orgDoc.id, 'staff');
        await setDoc(userOrgRef, userOrgData);
  
        return { 
          success: true, 
          organizationId: orgDoc.id,
          organizationName: orgData.name 
        };
      } catch (error) {
        console.error('組織参加エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * 組織のメンバー一覧を取得
     */
    static async getOrganizationMembers(organizationId, userId) {
      try {
        // 権限チェック
        const hasPermission = await this.checkUserPermission(userId, organizationId, 'org:read');
        if (!hasPermission) {
          return { success: false, error: 'メンバー情報を閲覧する権限がありません' };
        }
  
        const membersQuery = query(
          collection(db, 'userOrganizations'),
          where('organizationId', '==', organizationId),
          where('isActive', '==', true)
        );
        
        const membersSnap = await getDocs(membersQuery);
        const members = membersSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
  
        return { success: true, members };
      } catch (error) {
        console.error('メンバー取得エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * ユーザーの権限をチェック
     */
    static async checkUserPermission(userId, organizationId, permission) {
      try {
        const userOrgRef = doc(db, 'userOrganizations', `${userId}_${organizationId}`);
        const userOrgDoc = await getDoc(userOrgRef);
        
        if (!userOrgDoc.exists() || !userOrgDoc.data().isActive) {
          return false;
        }
  
        const userRole = userOrgDoc.data().role;
        
        // オーナーと管理者は全権限
        if (userRole === 'owner' || userRole === 'admin') {
          return true;
        }
  
        // 基本的な権限マトリックス
        const rolePermissions = {
          manager: [
            'org:read', 'staff:read', 'staff:write', 'schedule:read', 
            'schedule:write', 'surgery:read', 'surgery:write', 'report:read'
          ],
          staff: [
            'staff:read', 'schedule:read', 'surgery:read'
          ]
        };
  
        return rolePermissions[userRole]?.includes(permission) || false;
      } catch (error) {
        console.error('権限チェックエラー:', error);
        return false;
      }
    }
  
    /**
     * 組織からメンバーを削除
     */
    static async removeMember(organizationId, userId, targetUserId, currentUserId) {
      try {
        // 権限チェック
        const hasPermission = await this.checkUserPermission(currentUserId, organizationId, 'org:manage_users');
        if (!hasPermission) {
          return { success: false, error: 'メンバーを削除する権限がありません' };
        }
  
        const userOrgRef = doc(db, 'userOrganizations', `${targetUserId}_${organizationId}`);
        await updateDoc(userOrgRef, {
          isActive: false,
          removedAt: new Date(),
          removedBy: currentUserId
        });
  
        return { success: true };
      } catch (error) {
        console.error('メンバー削除エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * メンバーの役割を変更
     */
    static async updateMemberRole(organizationId, targetUserId, newRole, currentUserId) {
      try {
        // 権限チェック
        const hasPermission = await this.checkUserPermission(currentUserId, organizationId, 'org:manage_users');
        if (!hasPermission) {
          return { success: false, error: 'メンバーの役割を変更する権限がありません' };
        }
  
        const userOrgRef = doc(db, 'userOrganizations', `${targetUserId}_${organizationId}`);
        await updateDoc(userOrgRef, {
          role: newRole,
          updatedAt: new Date(),
          updatedBy: currentUserId
        });
  
        return { success: true };
      } catch (error) {
        console.error('役割更新エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * ユーザーを組織に追加（マイグレーション用）
     */
    static async addUserToOrganization(organizationId, userId, userOrgData) {
      try {
        const userOrgRef = doc(db, 'userOrganizations', `${userId}_${organizationId}`);
        await setDoc(userOrgRef, userOrgData);
        
        console.log('✅ ユーザーを組織に追加:', { userId, organizationId });
        return { success: true };
      } catch (error) {
        console.error('ユーザー組織追加エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * 【移行用】既存データを組織ベースに移行
     */
    static async migrateExistingData(userId) {
      try {
        console.log('🔄 データ移行を開始します...');
        
        // デフォルト組織があるかチェック
        const orgCheck = await getDoc(doc(db, 'organizations', 'default-hospital'));
        
        if (!orgCheck.exists()) {
          // デフォルト組織を作成
          const defaultOrgData = {
            id: 'default-hospital',
            name: 'メイン病院',
            type: 'hospital',
            settings: {
              timezone: 'Asia/Tokyo',
              language: 'ja',
              maxStaff: 200
            },
            contact: {
              email: '',
              phone: '',
              address: ''
            },
            createdAt: new Date(),
            isActive: true,
            isDefault: true,
            isMigrated: true
          };
          
          const orgRef = doc(db, 'organizations', 'default-hospital');
          await setDoc(orgRef, defaultOrgData);
          console.log('✅ デフォルト組織を作成しました');
        }
        
        return { success: true, organizationId: 'default-hospital' };
      } catch (error) {
        console.error('データ移行エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * 権限設定のヘルパー
     */
    static getRolePermissions(role) {
      const permissions = {
        owner: [
          'org:read', 'org:write', 'org:delete', 'org:manage_users',
          'staff:read', 'staff:write', 'staff:delete', 'staff:manage',
          'schedule:read', 'schedule:write', 'schedule:delete',
          'surgery:read', 'surgery:write', 'surgery:delete',
          'report:read', 'report:generate',
          'settings:read', 'settings:write'
        ],
        admin: [
          'org:read', 'org:write', 'org:manage_users',
          'staff:read', 'staff:write', 'staff:manage',
          'schedule:read', 'schedule:write', 'schedule:delete',
          'surgery:read', 'surgery:write', 'surgery:delete',
          'report:read', 'report:generate',
          'settings:read', 'settings:write'
        ],
        manager: [
          'org:read',
          'staff:read', 'staff:write',
          'schedule:read', 'schedule:write',
          'surgery:read', 'surgery:write',
          'report:read'
        ],
        staff: [
          'org:read',
          'staff:read',
          'schedule:read',
          'surgery:read',
          'report:read'
        ]
      };
      
      return permissions[role] || permissions.staff;
    }
  
    // ==========================================
    // 組織ベースデータ管理機能
    // ==========================================
  
    /**
     * 組織のサブコレクションパスを生成
     */
    static getOrgCollectionPath(organizationId, collectionName) {
      return `organizations/${organizationId}/${collectionName}`;
    }
  
    /**
     * 組織のコレクション参照を取得
     */
    static getOrgCollection(organizationId, collectionName) {
      return collection(db, this.getOrgCollectionPath(organizationId, collectionName));
    }
  
    /**
     * 組織のドキュメント参照を取得
     */
    static getOrgDoc(organizationId, collectionName, docId) {
      return doc(db, this.getOrgCollectionPath(organizationId, collectionName), docId);
    }
  
    // ==========================================
    // スタッフ管理
    // ==========================================
  
    /**
     * 組織のスタッフ一覧を取得
     */
    static async getStaffList(organizationId) {
      try {
        const staffSnap = await getDocs(this.getOrgCollection(organizationId, 'staffList'));
        return staffSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('スタッフ一覧取得エラー:', error);
        throw error;
      }
    }
  
    /**
     * スタッフを追加
     */
    static async addStaff(organizationId, staffId, staffData) {
      try {
        const staffRef = this.getOrgDoc(organizationId, 'staffList', staffId);
        await setDoc(staffRef, {
          ...staffData,
          organizationId,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        console.log('✅ スタッフ追加完了:', { organizationId, staffId });
      } catch (error) {
        console.error('スタッフ追加エラー:', error);
        throw error;
      }
    }
  
    /**
     * スタッフ情報を更新
     */
    static async updateStaff(organizationId, staffId, updateData) {
      try {
        const staffRef = this.getOrgDoc(organizationId, 'staffList', staffId);
        await updateDoc(staffRef, {
          ...updateData,
          updatedAt: new Date()
        });
        console.log('✅ スタッフ更新完了:', { organizationId, staffId });
      } catch (error) {
        console.error('スタッフ更新エラー:', error);
        throw error;
      }
    }
  
    /**
     * スタッフを削除
     */
    static async deleteStaff(organizationId, staffId) {
      try {
        const staffRef = this.getOrgDoc(organizationId, 'staffList', staffId);
        await deleteDoc(staffRef);
        console.log('✅ スタッフ削除完了:', { organizationId, staffId });
      } catch (error) {
        console.error('スタッフ削除エラー:', error);
        throw error;
      }
    }
  
    // ==========================================
    // シフト管理
    // ==========================================
  
    /**
     * 組織のシフトデータを取得
     */
    static async getShifts(organizationId, year = null, month = null) {
      try {
        let shiftsQuery = this.getOrgCollection(organizationId, 'shifts');
        
        if (year && month) {
          const monthId = `${year}年${month}月`;
          shiftsQuery = query(shiftsQuery, where('monthId', '==', monthId));
        }
        
        const shiftsSnap = await getDocs(shiftsQuery);
        return shiftsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('シフトデータ取得エラー:', error);
        throw error;
      }
    }
  
    /**
     * シフトデータを保存
     */
    static async saveShift(organizationId, shiftId, shiftData) {
      try {
        const shiftRef = this.getOrgDoc(organizationId, 'shifts', shiftId);
        await setDoc(shiftRef, {
          ...shiftData,
          organizationId,
          updatedAt: new Date()
        });
        console.log('✅ シフト保存完了:', { organizationId, shiftId });
      } catch (error) {
        console.error('シフト保存エラー:', error);
        throw error;
      }
    }
  
    // ==========================================
    // 手術・術式管理
    // ==========================================
  
    /**
     * 組織の術式一覧を取得
     */
    static async getProcedures(organizationId) {
      try {
        const proceduresSnap = await getDocs(this.getOrgCollection(organizationId, 'procedures'));
        return proceduresSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('術式一覧取得エラー:', error);
        throw error;
      }
    }
  
    /**
     * 組織の手術スケジュールを取得
     */
    static async getSurgeries(organizationId, date = null) {
      try {
        let surgeriesQuery = this.getOrgCollection(organizationId, 'surgeries');
        
        if (date) {
          surgeriesQuery = query(surgeriesQuery, where('date', '==', date));
        }
        
        const surgeriesSnap = await getDocs(surgeriesQuery);
        return surgeriesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('手術スケジュール取得エラー:', error);
        throw error;
      }
    }
  
    // ==========================================
    // スキル管理
    // ==========================================
  
    /**
     * 組織のスキル記録を取得
     */
    static async getSkillRecords(organizationId, userId = null) {
      try {
        let skillQuery = this.getOrgCollection(organizationId, 'skillRecords');
        
        if (userId) {
          skillQuery = query(skillQuery, where('userId', '==', userId));
        }
        
        const skillSnap = await getDocs(skillQuery);
        return skillSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('スキル記録取得エラー:', error);
        throw error;
      }
    }
  
    // ==========================================
    // 設定管理
    // ==========================================
  
    /**
     * 組織のシフトパターンを取得
     */
    static async getShiftPatterns(organizationId) {
      try {
        const patternsSnap = await getDocs(this.getOrgCollection(organizationId, 'shiftPatterns'));
        return patternsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('シフトパターン取得エラー:', error);
        throw error;
      }
    }
  
    /**
     * 組織の設定を取得
     */
    static async getOrganizationSettings(organizationId) {
      try {
        const settingsRef = this.getOrgDoc(organizationId, 'settings', 'general');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          return settingsDoc.data();
        } else {
          // デフォルト設定を返す
          return {
            timezone: 'Asia/Tokyo',
            language: 'ja',
            maxStaff: 100,
            autoAssignEnabled: true
          };
        }
      } catch (error) {
        console.error('組織設定取得エラー:', error);
        throw error;
      }
    }
  
    // ==========================================
    // データ移行用
    // ==========================================
  
    /**
     * 既存データを組織ベースに移行
     */
    static async migrateExistingDataToOrganization(organizationId, collectionName) {
      try {
        console.log(`🔄 ${collectionName}データの移行開始...`);
        
        // 既存の共有データを取得
        const existingSnap = await getDocs(collection(db, collectionName));
        const existingData = existingSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
  
        if (existingData.length === 0) {
          console.log(`📝 移行対象の${collectionName}データがありません`);
          return { success: true, migratedCount: 0 };
        }
  
        // バッチ処理で組織ベースコレクションにコピー
        const batch = writeBatch(db);
        let migratedCount = 0;
  
        existingData.forEach(item => {
          const orgDocRef = this.getOrgDoc(organizationId, collectionName, item.id);
          batch.set(orgDocRef, {
            ...item,
            organizationId,
            migratedAt: new Date(),
            originalCollectionId: item.id
          });
          migratedCount++;
        });
  
        await batch.commit();
        
        console.log(`✅ ${collectionName}データ移行完了: ${migratedCount}件`);
        return { success: true, migratedCount };
      } catch (error) {
        console.error(`${collectionName}データ移行エラー:`, error);
        return { success: false, error: error.message };
      }
    }
  
    /**
     * すべてのデータを一括移行
     */
    static async migrateAllDataToOrganization(organizationId) {
      try {
        console.log(`🚀 組織 ${organizationId} への全データ移行開始...`);
        
        const collections = [
          'staffList',
          'shifts', 
          'procedures',
          'surgeries',
          'skillRecords',
          'shiftPatterns',
          'departments',
          'skillGroups'
        ];
  
        const results = {};
        
        for (const collectionName of collections) {
          const result = await this.migrateExistingDataToOrganization(organizationId, collectionName);
          results[collectionName] = result;
          
          // 処理の間隔を空ける（Firebase制限対策）
          await new Promise(resolve => setTimeout(resolve, 500));
        }
  
        console.log('✅ 全データ移行完了:', results);
        return { success: true, results };
      } catch (error) {
        console.error('全データ移行エラー:', error);
        return { success: false, error: error.message };
      }
    }
  
    // ==========================================
    // ユーティリティ
    // ==========================================
  
    /**
     * 組織のデータ統計を取得
     */
    static async getOrganizationDataStats(organizationId) {
      try {
        const collections = ['staffList', 'shifts', 'procedures', 'surgeries', 'skillRecords'];
        const stats = {};
  
        for (const collectionName of collections) {
          const snap = await getDocs(this.getOrgCollection(organizationId, collectionName));
          stats[collectionName] = snap.size;
        }
  
        return stats;
      } catch (error) {
        console.error('データ統計取得エラー:', error);
        throw error;
      }
    }
  
    /**
     * 組織データの存在確認（改善版）
     */
    static async checkOrganizationDataExists(organizationId) {
      try {
        // 複数のコレクションをチェックして、いずれかにデータがあれば移行済みと判定
        const collections = ['staffList', 'shifts', 'procedures', 'shiftPatterns'];
        
        for (const collectionName of collections) {
          try {
            const snap = await getDocs(this.getOrgCollection(organizationId, collectionName));
            if (snap.size > 0) {
              console.log(`✅ ${collectionName}にデータが見つかりました (${snap.size}件)`);
              return true;
            }
          } catch (error) {
            console.warn(`${collectionName}のチェック中にエラー:`, error);
            // エラーが発生しても他のコレクションをチェック続行
          }
        }
        
        console.log('❌ すべてのコレクションにデータが見つかりませんでした');
        return false;
      } catch (error) {
        console.error('データ存在確認エラー:', error);
        return false;
      }
    }
  }